// Mobile-first "home screen widget" page for B&B Emmanuel.
//
// Pinned to the home screen on iOS/Android via PWA shortcut, this page
// gives the host a glance at TODAY's check-ins / check-outs / in-house
// guests with one-tap shortcuts to the full calendar, the channel
// manager and a "new walk-in" form.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { getToken } from "@/lib/auth";
import { format, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import { toast } from "sonner";
import { errMsg } from "@/lib/errors";
import {
  LogIn, LogOut, BedDouble, Car, AlertCircle, Calendar as CalIcon,
  Settings, Plus, RefreshCcw, ExternalLink, Download, Phone, MessageCircle,
  ChevronDown, ChevronUp,
} from "lucide-react";

const fmtDate = (iso) => {
  try { return format(parseISO(iso), "dd/MM/yyyy"); }
  catch { return iso; }
};

export default function AdminWidgetPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [today] = useState(new Date().toISOString().slice(0, 10));

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login?next=/admin/widget", { replace: true });
      return;
    }
    load();
    // Auto-refresh every 60s while the page is open
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    try {
      // Load main widget data + iCal inbox (Booking.com da assegnare) in parallel
      const [rWidget, rInbox] = await Promise.all([
        api.get("/admin/dashboard/next-7-days"),
        // Non-blocking: if ical-inbox fails (endpoint absent, network), fall back to empty list
        api.get("/admin/ical-inbox").catch(() => ({ data: { items: [] } })),
      ]);
      setData({ ...rWidget.data, ical_inbox: rInbox.data?.items || [] });
    } catch (e) {
      console.error("widget load", e);
    } finally {
      setLoading(false);
    }
  };

  const [syncing, setSyncing] = useState(false);
  const syncBooking = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const r = await api.post("/admin/bookings/poll-booking-email?include_read=true&days_back=2");
      const created = r.data?.created || 0;
      const updated = r.data?.updated || 0;
      const cancelled = r.data?.cancelled || 0;
      if (created || updated || cancelled) {
        toast.success(`📥 ${created} nuove, ${updated} aggiornate, ${cancelled} cancellate`);
        await load();
      } else {
        toast.info("Nessuna nuova prenotazione Booking nelle ultime 48h");
      }
    } catch (e) {
      toast.error(errMsg(e, "Errore sincronizzazione"));
    } finally {
      setSyncing(false);
    }
  };

  // iter227 — Logica "check-in vs in casa" allineata al comportamento
  // richiesto dall'host:
  //  - "Check-in di oggi" = SOLO chi deve ancora arrivare (status
  //    pending/confirmed). Se l'ospite ha già fatto check-in via App
  //    Guida (`status === "checked_in"`) NON deve più apparire qui.
  //  - "In casa stanotte" = tutti quelli che effettivamente dormiranno
  //    stanotte, INCLUSI quelli che sono arrivati oggi e hanno già
  //    fatto check-in (Giulia via Guida). Prima il backend escludeva
  //    esplicitamente il giorno di check-in dalla in_house list
  //    (server.py:6105 `if d != ci and d != co`) → il frontend
  //    ricostruisce la lista completa qui.
  //  - iter231 — ULTERIORE FIX: chi ha check_in === oggi ma NON ha
  //    ancora fatto check-in (status != "checked_in") NON deve
  //    apparire in "In casa stanotte" (prima appariva perché il
  //    backend lo aggiungeva comunque al 25/08 come stay_date).
  const checkInsToday = (data?.check_ins || []).filter(
    (c) => c.check_in === today && c.status !== "checked_in",
  );
  const checkOutsToday = (data?.check_outs || []).filter((c) => c.check_out === today);
  // Ospiti già "in casa" secondo il backend (arrivati in giorni precedenti).
  const inHouseFromBackend = (data?.in_house || []).filter((c) => {
    if (c.stay_date !== today) return false;
    // iter231 — se questo è il giorno di check-in e non ha ancora
    // fatto check-in, NON è in casa: è ancora in arrivo.
    if (c.check_in === today && c.status !== "checked_in") return false;
    return true;
  });
  // Aggiungi anche chi ha fatto check-in OGGI (arrivati adesso, già dentro).
  // Deduplica per booking code per evitare doppie card.
  const checkedInToday = (data?.check_ins || []).filter(
    (c) => c.check_in === today && c.status === "checked_in",
  ).map((c) => ({ ...c, stay_date: today }));
  const seenCodes = new Set(inHouseFromBackend.map((c) => c.code));
  const inHouseToday = [
    ...inHouseFromBackend,
    ...checkedInToday.filter((c) => !seenCodes.has(c.code)),
  ];
  // iter218 — check-out di domani, utile per programmare la sera le pulizie
  const _tomorrow = (() => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  })();
  const checkOutsTomorrow = (data?.check_outs || []).filter((c) => c.check_out === _tomorrow);
  const shuttlesToday = (data?.shuttles || []).filter((s) => s.date === today);
  const icalInbox = data?.ical_inbox || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FDFBF7] to-[#F4EFE6] pb-24">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-[#1E3A5F] text-white shadow-lg">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest opacity-70">B&B Emmanuel</div>
            <h1 className="font-display text-lg leading-tight">Oggi · {fmtDate(today)}</h1>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={syncBooking}
              className="p-2 rounded-full hover:bg-white/10"
              data-testid="widget-sync-booking"
              aria-label="Sincronizza Booking"
              title="Sincronizza email Booking"
            >
              <Download className={`w-4 h-4 ${syncing ? "animate-bounce" : ""}`} />
            </button>
            <button
              onClick={load}
              className="p-2 rounded-full hover:bg-white/10"
              data-testid="widget-refresh"
              aria-label="Aggiorna"
            >
              <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={() => navigate("/admin/dashboard")}
              className="p-2 rounded-full hover:bg-white/10"
              data-testid="widget-settings"
              aria-label="Dashboard"
            >
              <Settings className="w-4 h-4" />
            </button>
          </div>
        </div>
        {/* Counters strip. iter223 — usa le stesse liste filtrate per OGGI
            che compaiono sotto (checkInsToday/checkOutsToday/inHouseToday/
            shuttlesToday) invece di `counts.*` che il backend calcola
            sull'intera finestra di 7 giorni. Prima l'host vedeva "2
            check-out" nel contatore ma "Nessuna partenza oggi" nella
            lista → discrepanza confondente. */}
        <div className="max-w-md mx-auto px-4 pb-3 grid grid-cols-4 gap-2 text-center">
          <Counter value={checkInsToday.length} label="check-in" tone="emerald" />
          <Counter value={checkOutsToday.length} label="check-out" tone="amber" />
          <Counter value={inHouseToday.length} label="in casa" tone="violet" />
          <Counter value={shuttlesToday.length} label="navette" tone="sky" />
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-4 space-y-4">
        {/* CHECK-IN OGGI — sempre APERTA di default */}
        <Section
          icon={LogIn}
          title="Check-in di oggi"
          tone="emerald"
          empty="Nessun arrivo oggi"
          items={checkInsToday}
          defaultOpen={true}
          testIdBase="widget-checkins"
          render={(b) => <BookingRow b={b} sub={`Arriva oggi · ${b.guests || 1}p`} waKind="checkin" testId={`widget-ci-${b.code}`} />}
        />

        {/* iCal DA ASSEGNARE — Booking.com senza nome/codice reali, in attesa di
            essere trasformati in prenotazioni vere (via CSV o manuale). Tap
            sulla riga → apri iCal Inbox admin per gestire. */}
        {icalInbox.length > 0 && (
          <Section
            icon={AlertCircle}
            title="iCal da trasformare"
            tone="rose"
            items={icalInbox}
            defaultOpen={true}
            testIdBase="widget-ical"
            render={(it) => <IcalInboxRow item={it} testId={`widget-ical-${it.uid}`} />}
          />
        )}

        {/* IN CASA STANOTTE — chiusa di default (contiene molti) */}
        <Section
          icon={BedDouble}
          title="In casa stanotte"
          tone="violet"
          empty="Nessun ospite in casa"
          items={inHouseToday}
          defaultOpen={false}
          testIdBase="widget-inhouse"
          render={(b) => <BookingRow b={b} sub={`Fino a ${fmtDate(b.check_out)} · ${b.guests || 1}p`} waKind="inhouse" testId={`widget-ih-${b.code}`} />}
        />

        {/* CHECK-OUT OGGI — chiusa di default */}
        <Section
          icon={LogOut}
          title="Check-out di oggi"
          tone="amber"
          empty="Nessuna partenza oggi"
          items={checkOutsToday}
          defaultOpen={false}
          testIdBase="widget-checkouts"
          render={(b) => <BookingRow b={b} sub={`Parte oggi · ${b.guests || 1}p`} waKind="checkout" testId={`widget-co-${b.code}`} />}
        />

        {/* CHECK-OUT DOMANI — iter218, per programmare la sera le pulizie */}
        <Section
          icon={LogOut}
          title="Check-out di domani"
          tone="orange"
          empty="Nessuna partenza domani"
          items={checkOutsTomorrow}
          defaultOpen={false}
          testIdBase="widget-checkouts-tomorrow"
          render={(b) => <BookingRow b={b} sub={`Parte domani · ${b.guests || 1}p`} waKind="checkout" testId={`widget-cot-${b.code}`} />}
        />

        {/* NAVETTE */}
        {shuttlesToday.length > 0 && (
          <Section
            icon={Car}
            title="Navette di oggi"
            tone="sky"
            items={shuttlesToday}
            render={(s) => (
              <div className="bg-white border border-sky-200 rounded-lg p-3" data-testid={`widget-shuttle-${s.id || s.time}`}>
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-sky-900">{s.time || "—"} · {s.direction === "pickup" ? "Pickup" : "Dropoff"}</div>
                  <div className="text-xs text-sky-700">€{s.price || 10}</div>
                </div>
                <div className="text-xs text-[#635F59]">{s.guest_name || "—"}</div>
              </div>
            )}
          />
        )}

        {/* QUICK ACTIONS */}
        <div className="grid grid-cols-2 gap-2 pt-2">
          <ActionButton
            icon={CalIcon}
            label="Calendario"
            color="#1E3A5F"
            onClick={() => navigate("/admin/dashboard?tab=calendar")}
            testId="widget-action-calendar"
          />
          <ActionButton
            icon={Plus}
            label="Nuova prenotazione"
            color="#AC5D45"
            onClick={() => navigate("/admin/dashboard?tab=calendar&action=new")}
            testId="widget-action-new"
          />
          <ActionButton
            icon={BedDouble}
            label="Channel Manager"
            color="#506B5E"
            onClick={() => navigate("/admin/dashboard?tab=channel")}
            testId="widget-action-channel"
          />
          <ActionButton
            icon={ExternalLink}
            label="Dashboard completa"
            color="#635F59"
            onClick={() => navigate("/admin/dashboard")}
            testId="widget-action-dashboard"
          />
        </div>
      </main>
    </div>
  );
}

function Counter({ value, label, tone }) {
  const tones = {
    emerald: "bg-emerald-500/20 text-emerald-100 border-emerald-300/30",
    amber: "bg-amber-500/20 text-amber-100 border-amber-300/30",
    violet: "bg-violet-500/20 text-violet-100 border-violet-300/30",
    sky: "bg-sky-500/20 text-sky-100 border-sky-300/30",
  };
  return (
    <div className={`rounded-lg border py-2 ${tones[tone]}`}>
      <div className="font-display text-xl leading-none">{value}</div>
      <div className="text-[9px] uppercase tracking-wider opacity-80">{label}</div>
    </div>
  );
}

function Section({ icon: Icon, title, tone, empty, items, render, defaultOpen = true, testIdBase }) {
  const [open, setOpen] = useState(defaultOpen);
  const tones = {
    emerald: "text-emerald-700 border-emerald-200",
    amber: "text-amber-700 border-amber-200",
    violet: "text-violet-700 border-violet-200",
    sky: "text-sky-700 border-sky-200",
    rose: "text-rose-700 border-rose-300",
    orange: "text-orange-700 border-orange-300",
  };
  return (
    <section className={`bg-white/60 backdrop-blur rounded-xl border ${tones[tone]} p-3 shadow-sm`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 py-1 -my-1"
        data-testid={testIdBase ? `${testIdBase}-toggle` : undefined}
      >
        <Icon className={`w-5 h-5 ${tones[tone].split(" ")[0]}`} />
        <h2 className="font-display font-bold text-base text-[#1E3A5F] tracking-tight">{title}</h2>
        {items.length > 0 && <span className="ml-auto text-[11px] font-bold text-white rounded-full px-2 py-0.5 bg-[#AC5D45]">{items.length}</span>}
        {open
          ? <ChevronUp className={`w-5 h-5 ${items.length > 0 ? "" : "ml-auto"} ${tones[tone].split(" ")[0]}`} strokeWidth={2.5} />
          : <ChevronDown className={`w-5 h-5 ${items.length > 0 ? "" : "ml-auto"} ${tones[tone].split(" ")[0]}`} strokeWidth={2.5} />
        }
      </button>
      {open && (
        <div className="mt-2">
        {items.length === 0 ? (
          <div className="text-xs text-[#635F59] italic px-1 py-2">{empty}</div>
        ) : (
          <div className="space-y-1.5">
            {items.map((it, i) => (
              <div key={(it.code || it.id || it.uid || i) + "-" + i}>{render(it)}</div>
            ))}
          </div>
        )}
        </div>
      )}
    </section>
  );
}

// iter197 — Messaggi WhatsApp pre-compilati per il widget del giorno.
// Ogni messaggio è dedicato a un contesto (check-in / check-out / in-casa)
// e usa il nome dell'ospite. Il link alla Guida Home è cliccabile su
// WhatsApp perché è un URL nudo (WhatsApp ne fa auto-preview).
const GUIDE_HOME_URL = "https://guida-home-beb.pages.dev/";

const buildWhatsAppMessage = (firstName, kind) => {
  const nome = firstName || "";
  const hello = nome ? `Ciao ${nome}` : "Ciao";
  if (kind === "checkin") {
    return (
      `${hello}, la tua camera è pronta per il check-in ✨\n\n` +
      `Trovi tutte le info sul soggiorno ed effettui check-in e check-out dalla nostra Guida Home:\n` +
      `👉 ${GUIDE_HOME_URL}\n\n` +
      `Gentilmente comunicaci qui su WhatsApp il tuo orario di arrivo previsto.\n` +
      `Grazie e buon viaggio! 🌊\n— B&B Emmanuel`
    );
  }
  if (kind === "checkout") {
    return (
      `${hello}, oggi è il giorno del check-out 🌅\n\n` +
      `Ti ricordiamo di lasciare la chiave in camera e chiudere la porta. ` +
      `Per il check-out finale usa la nostra Guida Home:\n👉 ${GUIDE_HOME_URL}\n\n` +
      `Grazie di essere stato con noi — ti aspettiamo alla prossima!\n— B&B Emmanuel`
    );
  }
  // in-house / default
  return (
    `${hello}, tutto bene con la camera? 😊\n\n` +
    `Se ti serve qualcosa (asciugamani, colazione extra, info Reggio, transfer) ` +
    `scrivici qui su WhatsApp o consulta la Guida Home:\n👉 ${GUIDE_HOME_URL}\n\n` +
    `Buona giornata!\n— B&B Emmanuel`
  );
};

// wa.me richiede solo cifre — normalizziamo eventuali "+39 ...", spazi, /.
const normalizePhoneForWa = (raw) => {
  if (!raw) return "";
  const digits = String(raw).replace(/\D/g, "");
  if (!digits) return "";
  // Se comincia con "39" o è già lunghezza estera → OK.
  // Se è un numero italiano senza prefisso (10 cifre iniziali 3 per cellulare o 0 per fisso) → aggiungiamo 39.
  if (digits.startsWith("00")) return digits.slice(2);
  if (digits.length === 10 && (digits.startsWith("3") || digits.startsWith("0"))) return "39" + digits;
  return digits;
};

function BookingRow({ b, sub, waKind, testId }) {
  const navigate = useNavigate();
  const grn = b.guide_room_number;
  const waPhone = normalizePhoneForWa(b.guest?.phone || b.phone || "");
  const firstName = b.guest?.first_name || (b.guest_name || "").split(" ")[0] || "";
  const waHref = waPhone
    ? `https://wa.me/${waPhone}?text=${encodeURIComponent(buildWhatsAppMessage(firstName, waKind || "checkin"))}`
    : "";
  const openBooking = () => {
    // Deep-link to Admin Dashboard bookings tab, pre-filled with this
    // booking code so the row appears immediately and the host can tap
    // the pencil / eye icon to see the full reservation.
    const code = b.code || b.id;
    if (!code) return;
    navigate(`/admin/dashboard?tab=bookings&code=${encodeURIComponent(code)}`);
  };
  return (
    <button
      type="button"
      onClick={openBooking}
      className="w-full text-left bg-white border border-[#E8E2D9] rounded-lg p-3 flex items-center gap-3 hover:bg-[#FFF8F0] active:scale-[0.99] transition"
      data-testid={testId}
    >
      {grn != null && grn !== "" && (
        <div className="shrink-0 w-14 h-14 rounded-lg bg-[#AC5D45] text-white flex items-center justify-center font-display text-xl leading-none">
          n{grn}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="font-bold text-[#1E3A5F] truncate text-base leading-tight">
          {b.guest_name || `${b.guest?.first_name || ""} ${b.guest?.last_name || ""}`.trim() || "Ospite"}
        </div>
        <div className="text-xs text-[#635F59] truncate mt-0.5">
          {b.room_name} · {sub}
        </div>
      </div>
      {b.guest?.phone && (
        <a
          href={`tel:${b.guest.phone}`}
          className="shrink-0 w-11 h-11 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 flex items-center justify-center hover:bg-emerald-100 active:scale-95 transition"
          onClick={(e) => e.stopPropagation()}
          data-testid={`${testId}-call`}
          aria-label="Chiama ospite"
          title="Chiama"
        >
          <Phone className="w-5 h-5" />
        </a>
      )}
      {waHref && (
        <a
          href={waHref}
          target="_blank"
          rel="noreferrer"
          className="shrink-0 w-11 h-11 rounded-lg bg-[#25D366]/10 border border-[#25D366]/40 text-[#128C7E] flex items-center justify-center hover:bg-[#25D366]/20 active:scale-95 transition"
          onClick={(e) => e.stopPropagation()}
          data-testid={`${testId}-wa`}
          aria-label="Scrivi su WhatsApp"
          title="Scrivi su WhatsApp"
        >
          <MessageCircle className="w-5 h-5" />
        </a>
      )}
      <span className="shrink-0 text-[#AC5D45] text-xl leading-none ml-1" aria-hidden="true">›</span>
    </button>
  );
}

// iter198 — Riga iCal Inbox nel widget "Oggi".
// Le prenotazioni Booking.com arrivano via iCal SENZA nome ospite né codice
// reale. La camera è già bloccata ma va trasformata in prenotazione (via
// CSV Booking o manuale). Al tap: navigate all'admin → tab iCal Inbox,
// dove l'host può cliccare "+ Crea manuale" o scaricare il CSV.
function IcalInboxRow({ item, testId }) {
  const navigate = useNavigate();
  const grn = item.guide_room_number;
  const roomName = item.room_name || "Camera";
  const start = item.start ? fmtDate(item.start) : "—";
  const end = item.end ? fmtDate(item.end) : "—";
  const source = item.source || "Booking.com";
  const goToInbox = () => {
    // iter201 — Deep-link con UID così l'host arriva già sull'entry giusta
    const uid = item.uid ? `&uid=${encodeURIComponent(item.uid)}` : "";
    navigate(`/admin/dashboard?tab=ical-inbox${uid}`);
  };
  return (
    <button
      type="button"
      onClick={goToInbox}
      className="w-full text-left bg-white border border-rose-200 rounded-lg p-3 flex items-center gap-3 hover:bg-rose-50 active:scale-[0.99] transition"
      data-testid={testId}
    >
      {grn != null && grn !== "" ? (
        <div className="shrink-0 w-14 h-14 rounded-lg bg-[#AC5D45] text-white flex items-center justify-center font-display text-xl leading-none">
          n{grn}
        </div>
      ) : (
        <div className="shrink-0 w-14 h-14 rounded-lg border-2 border-dashed border-rose-300 text-rose-500 flex items-center justify-center font-display text-xs text-center leading-tight px-1">
          da<br/>assegnare
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="font-bold text-[#1E3A5F] truncate text-base leading-tight">
          {source} — da trasformare
        </div>
        <div className="text-sm text-[#635F59] truncate mt-0.5">
          {roomName} · {start} → {end}
        </div>
      </div>
      <span className="shrink-0 text-rose-500 text-xl leading-none ml-1" aria-hidden="true">›</span>
    </button>
  );
}

function ActionButton({ icon: Icon, label, color, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className="bg-white border border-[#E8E2D9] rounded-xl p-3 flex flex-col items-center gap-2 shadow-sm hover:shadow active:scale-95 transition"
      data-testid={testId}
    >
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center text-white"
        style={{ background: color }}
      >
        <Icon className="w-5 h-5" />
      </div>
      <span className="text-xs font-medium text-[#1E3A5F] text-center leading-tight">{label}</span>
    </button>
  );
}
